import { resetState, setBookingType } from '@src/components/OMS/ManualDealTradeView/ManualDealSlice';
import { openView } from '@src/components/OMS/OMSSlice';
import { OMSView, type OMSViewLaunchDataMap } from '@src/components/OMS/OMSView';
import { resolveUsersSentinel } from '@src/hooks/filters/useUsersFilter';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import { useUser } from '@src/hooks/useUser';
import { useAppStateDispatch } from '@src/providers/AppStateProvider/AppStateProviderHooks';
import type { OTCDealData } from '@src/types/OTC';
import {
  ACTION,
  BlotterDensity,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEAL,
  DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
  ExportMenuItems,
  filterByColumnMainMenuItems,
  FormControlSizes,
  IconName,
  isGridApiReady,
  MixpanelEvent,
  removeEmptyFilters,
  SubProductTypeEnum,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useConstant,
  useDateRangeFilter,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type BlotterTableFilter,
} from '@talos/kyoko';
import type { GetMainMenuItemsParams, SelectionChangedEvent } from 'ag-grid-enterprise';
import { useCallback, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { REPO_INSPECTOR_DRAWER_TITLE, useInstrumentInspectorDrawer } from '../InstrumentInspectorDrawer';
import type { DealBlotterRequest } from '../otcBlotterTypes';
import { useRepoColumns } from './useRepoColumns';
import { colIDToFilterBuilderKey, onlyServerFilterKeys, useRepoFilter } from './useRepoFilter';
import { useRepoMenu } from './useRepoMenu';

interface FilteredReposParams {
  blotterPersistID: string;
  tabLabel?: string;
  defaultFilter: BlotterTableFilter;
  onCloneTab?: () => void;
  initialIsOpen?: boolean;
  persistFilter?: boolean;
  layoutId?: string;
}

export const FilteredRepos = function FilteredRepos({
  blotterPersistID,
  tabLabel,
  defaultFilter,
  onCloneTab,
  initialIsOpen,
  persistFilter,
  layoutId,
}: FilteredReposParams) {
  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();
  const { isAuthorized } = useRoleAuth();
  const canSubmitOrder = isAuthorized(ACTION.SUBMIT_ORDER);
  const { Name: currentUserName } = useUser();
  const blotterTag = useConstant(`FILTERED_OTCRepo_${uuid()}`);

  const columns = useRepoColumns();

  const persistedBlotterTable = usePersistedBlotterTable(
    blotterPersistID,
    { filter: defaultFilter },
    { persistFilter }
  );

  const filteredRepos = useRepoFilter({
    persistedBlotterTable,
  });

  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filteredRepos;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const { openClause } = filterBuilderAccordion;

  const getExtraMainMenuItems = useCallback(
    (params: GetMainMenuItemsParams) => {
      return filterByColumnMainMenuItems({
        params,
        colIDToFilterBuilderKey,
        openClause,
        mixpanel,
      });
    },
    [mixpanel, openClause]
  );

  const handleBookRepo = useCallback(() => {
    mixpanel.track(MixpanelEvent.BookManualTrade);
    dispatch(resetState());
    dispatch(setBookingType('repo'));
    const launchData: OMSViewLaunchDataMap[typeof OMSView.ManualDealTradeForm] = { bookingType: 'repo' };
    dispatch(
      openView({
        view: OMSView.ManualDealTradeForm,
        launchData,
      })
    );
  }, [dispatch, mixpanel]);

  const { openInstrumentInspectorDrawer } = useInstrumentInspectorDrawer();

  const openRepoInstrumentInspectorDrawer = useCallback(
    (deal: OTCDealData, onlyIfOpen?: boolean) =>
      openInstrumentInspectorDrawer(deal, REPO_INSPECTOR_DRAWER_TITLE, onlyIfOpen),
    [openInstrumentInspectorDrawer]
  );

  const repoMenu = useRepoMenu({
    openClause,
    filterableProperties: filterBuilderProps.properties,
    openRepoInstrumentInspectorDrawer,
    layoutId,
  });

  const onSelectionChanged = useCallback(
    ({ api }: SelectionChangedEvent<OTCDealData>) => {
      if (!isGridApiReady(api)) {
        return;
      }
      const rows = api.getSelectedRows();
      if (rows.length === 1) {
        openRepoInstrumentInspectorDrawer(rows[0], true);
      }
    },
    [openRepoInstrumentInspectorDrawer]
  );

  const columnsWithMenu = useMemo(() => [...columns, ...repoMenu.columns], [columns, repoMenu.columns]);

  const request = useMemo(
    () =>
      ({
        name: DEAL,
        tag: blotterTag,
        sort_by: '-TradeTime',
        SubProductTypes: [SubProductTypeEnum.OTCRepo],
        ...removeEmptyFilters(
          onlyServerFilterKeys({
            ...filteredRepos.filter,
            Users: resolveUsersSentinel(filteredRepos.filter.Users, currentUserName),
          })
        ),
      }) satisfies DealBlotterRequest,
    [blotterTag, filteredRepos.filter, currentUserName]
  );

  const blotterTable = useWsBlotterTable({
    request,
    rowID: 'ID',
    columns: columnsWithMenu,
    clientLocalFilter,
    getExtraMainMenuItems,
    persistence: persistedBlotterTable,
    density: BlotterDensity.Compact,
    contextMenuOptions: {
      getCustomContextMenuItems: repoMenu.getCustomContextMenuItems,
      dataDisplayCallback: repoMenu.dataDisplayCallback,
    },
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
      onSelectionChanged,
      onRowDoubleClicked: ({ data }) => {
        data && openRepoInstrumentInspectorDrawer(data);
      },
    },
    exportOptions: {
      name: 'Repos',
      tabLabel,
    },
  });

  const dateRangeFilter = useDateRangeFilter(filteredRepos.filter, filteredRepos.changeFilter);

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        {...dateRangeFilter}
        quickFilterPrefix={
          canSubmitOrder && (
            <Button
              startIcon={IconName.DocumentAdd}
              variant={ButtonVariants.Default}
              size={FormControlSizes.Small}
              onClick={handleBookRepo}
            >
              Book Repo
            </Button>
          )
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      {repoMenu.dialogs}
    </>
  );
};
